import React, { useEffect, useState } from "react";
import { mockNotifications, mockPosts } from "../../data/mockData";
import "../../App.css";
import "./notifications.css";
import { useAuth } from "../../App";
import { useNavigate } from 'react-router-dom';
import Lightbox from '../common/Lightbox';
import { formatDateVN } from '../../utils/formatDate';

export default function Notifications() {
  const { user, normalizeUserUrl, apiUrl: ctxApiUrl } = useAuth();
  const apiUrl = ctxApiUrl || process.env.REACT_APP_API_URL;
  const [notes, setNotes] = useState([]);
  const [posts, setPosts] = useState([]);
  const [teacherClassMap, setTeacherClassMap] = useState({});
  const [loading, setLoading] = useState(false);
  // preview modal state for Mods when reviewing pending posts
  const [previewPost, setPreviewPost] = useState(null); // post object or minimal { id, title, content, attachments }
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const navigate = useNavigate();

  // helper: ensure attachment urls point to api domain when path is relative
  function normalizeAttachmentUrl(a) {
    if (!a) return a;
    const copy = { ...a };
    const src = copy.url || copy.path || '';
    if (src && src.startsWith('/')) {
      const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
      copy.url = base + src;
    } else if (src && !src.match(/^https?:\/\//)) {
      const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
      copy.url = base + '/' + src.replace(/^\//, '');
    } else {
      copy.url = src;
    }
    // infer filename from url/path
    try {
      if (!copy.filename && copy.url) {
        const parts = String(copy.url).split('?')[0].split('/');
        copy.filename = parts[parts.length - 1] || copy.filename;
      }
    } catch (e) { }
    // infer mime type from file extension when missing so images/videos still render
    try {
      if (!copy.mime && copy.url) {
        const name = String(copy.url).split('?')[0];
        const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
        const imgExt = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
        const vidExt = ['mp4', 'webm', 'ogg', 'mov'];
        if (imgExt.includes(ext)) {
          copy.mime = (ext === 'jpg') ? 'image/jpeg' : `image/${ext}`;
        } else if (vidExt.includes(ext)) {
          copy.mime = `video/${ext}`;
        }
      }
    } catch (e) { }
    return copy;
  }

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!apiUrl || !user) { setNotes(mockNotifications); setPosts(mockPosts); return; }
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) return setNotes([]);
        const res = await fetch(`${apiUrl}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        if (mounted) setNotes((data.notifications || []).map(n => ({ ...n, read: (n.read === true) || (n.isRead === true) || Boolean(n.readAt) })));
      } catch (e) { console.error(e); setNotes([]); }
      finally { if (mounted) setLoading(false); }
    }
    load();
    return () => mounted = false;
  }, [apiUrl, user]);

  // fetch posts (used to show post titles in aggregated notifications)
  useEffect(() => {
    let mounted = true;
    async function loadPosts() {
      if (!apiUrl) { setPosts(mockPosts); return; }
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${apiUrl}/api/posts`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (mounted) setPosts(data.posts || []);
      } catch (e) { console.error(e); }
    }
    loadPosts();
    return () => { mounted = false; };
  }, [apiUrl]);

  // Ensure we fetch post details for referenced post IDs that are missing from `posts`.
  // This avoids showing fallback "bài viết #id" when we can fetch the real title.
  const _requestedPostIds = React.useRef(new Set());
  useEffect(() => {
    if (!apiUrl) return; // can't fetch without API
    if (!notes || !notes.length) return;
    const idsToFetch = [];
    for (const n of notes) {
      const refId = n.refId || n.ref_id || (n.sourceIds && n.sourceIds.length ? (() => {
        const src = notes.find(x => (n.sourceIds || []).includes(x.id) && (x.refId || x.ref_id));
        return src ? (src.refId || src.ref_id) : null;
      })() : null);
      if (!refId) continue;
      const exists = (posts || []).some(p => String(p.id) === String(refId));
      if (!exists && !_requestedPostIds.current.has(String(refId))) {
        idsToFetch.push(refId);
        _requestedPostIds.current.add(String(refId));
      }
    }
    if (!idsToFetch.length) return;
    (async () => {
      for (const id of idsToFetch) {
        try {
          await fetchPostById(id);
        } catch (e) { /* ignore individual failures */ }
      }
    })();
  }, [notes, posts, apiUrl]);

  // For Mods: fetch classes to map teacher usernames -> class name(s) for better notification display
  useEffect(() => {
    let mounted = true;
    if (!apiUrl || !user || user.role !== 'Mod') return () => { mounted = false; };
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${apiUrl}/api/classes`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (!mounted) return;
        const map = {};
        (data.classes || []).forEach(c => {
          if (!c) return;
          const tu = c.teacher_username || c.teacher || null;
          if (!tu) return;
          const name = c.name || c.class_name || null;
          if (!name) return;
          if (!map[tu]) map[tu] = name; else map[tu] = map[tu] + ', ' + name;
        });
        setTeacherClassMap(map);
      } catch (e) { console.error('failed fetch classes for notifications', e); }
    })();
    return () => { mounted = false; };
  }, [apiUrl, user]);

  // Poll notifications every 5 minutes to update the list and perform aggregation
  useEffect(() => {
    if (!apiUrl) return undefined; // when using mock data, no polling needed
    let mounted = true;
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${apiUrl}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (mounted) setNotes((data.notifications || []).map(n => ({ ...n, read: (n.read === true) || (n.isRead === true) || Boolean(n.readAt) })));
      } catch (e) { console.error('failed polling notifications', e); }
    }, 300000); // 5 minutes
    return () => { mounted = false; clearInterval(interval); };
  }, [apiUrl]);

  async function onClickNotification(n) {
    // mark read if API
    // n may be an aggregated notification (with sourceIds)
    const idsToMark = n.sourceIds && n.sourceIds.length ? n.sourceIds : (n.id ? [n.id] : []);
    if (idsToMark.length) {
      // Optimistically update local UI
      setNotes(prev => prev.map(nn => idsToMark.includes(nn.id) ? { ...nn, read: true } : nn));

      // If API is available, persist the read flags on server (batch endpoint)
      if (apiUrl) {
        (async () => {
          try {
            const token = localStorage.getItem('token');
            if (!token) return;
            await fetch(`${apiUrl}/api/notifications/read`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ ids: idsToMark })
            });
            // optionally refresh list later, but optimistic update is sufficient
          } catch (e) { console.error('Failed to persist notification read state', e); }
        })();
      }
    }

    // determine referenced post id (if any)
    const refId = n.refId || n.ref_id || (n.sourceRefId || null) || (n.sourceIds && n.sourceIds.length ? (() => {
      const src = notes.find(x => (n.sourceIds || []).includes(x.id) && (x.refId || x.ref_id));
      return src ? (src.refId || src.ref_id) : null;
    })() : null);

    // helper to open preview modal for Mods
    async function openPreviewForPostId(id) {
      if (!id) return;
      // if user is not Mod, just navigate
      if (!(user && user.role === 'Mod')) return navigate(`/posts/${id}`);

      // try to fetch full post from server; if fails, still open a minimal preview with id
      setPreviewLoading(true);
      try {
        // reuse normalized fetch helper so attachments are normalized the same way as Home
        const normalized = await fetchPostById(id);
        if (normalized) {
          setPreviewPost(normalized);
          setPreviewOpen(true);
          return;
        }
      } catch (e) {
        console.error('fetchPost failed', e);
      } finally {
        setPreviewLoading(false);
      }

      // fallback minimal preview (no attachments)
      setPreviewPost({ id, title: `Bài viết #${id}`, content: '', attachments: [] });
      setPreviewOpen(true);
    }

    // If the notification's message suggests a pending post, open preview for Mods rather than navigating
    const isPendingText = /chờ duyệt|pending|đợi duyệt|chua duyet|chưa duyệt|unapproved/i.test(String(n.message || ''));
    if (refId && user && user.role === 'Mod' && isPendingText) {
      await openPreviewForPostId(refId);
      return;
    }

    // otherwise navigate to post if referenced
    if (refId) return navigate(`/posts/${refId}`);
  }

  // Approve / reject helpers used by the preview modal
  async function approvePost(postId) {
    if (!postId || !apiUrl) return;
    try {
      const token = localStorage.getItem('token'); if (!token) return;
      const res = await fetch(`${apiUrl}/api/posts/${postId}/approve`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('approve failed');
      const data = await res.json();
      updatePostInState(postId, x => ({ ...x, approved: true }));
      // optimistic: mark related notifications read
      setNotes(prev => prev.map(n => {
        const rid = n.refId || n.ref_id;
        if (String(rid) === String(postId)) return { ...n, read: true };
        return n;
      }));
      try { await fetchPostById(postId); await fetchPending(); await fetchPostsFromApi(); signalPendingChange(); } catch (e) { /* ignore */ }
      setPreviewOpen(false); setPreviewPost(null);
      if (window.showToast) window.showToast('Approved', 'success');
      if (user && user.role === 'Teacher') { try { window.location.reload(); } catch (e) { } }
    } catch (e) { console.error('approvePost failed', e); if (window.showToast) window.showToast('Approval failed', 'error'); }
  }

  async function rejectPost(postId) {
    if (!postId || !apiUrl) return;
    try {
      const token = localStorage.getItem('token'); if (!token) return;
      const res = await fetch(`${apiUrl}/api/posts/${postId}/reject`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('reject failed');
      updatePostInState(postId, p => ({ ...p, approved: false }));
      setNotes(prev => prev.map(n => {
        const rid = n.refId || n.ref_id;
        if (String(rid) === String(postId)) return { ...n, read: true };
        return n;
      }));
      try { await fetchPending(); await fetchPostsFromApi(); signalPendingChange(); } catch (e) { }
      setPreviewOpen(false); setPreviewPost(null);
      if (window.showToast) window.showToast('Rejected post', 'success');
    } catch (e) { console.error('rejectPost failed', e); if (window.showToast) window.showToast('Rejection failed', 'error'); }
  }

  // helper: normalize sender name from various possible fields
  function getSenderName(n) {
    if (!n) return null;
    // if sender is an object with first/last name, prefer last_name + first_name
    const possibleSender = n.sender || n.from || n.user || n.actor || null;
    if (possibleSender && typeof possibleSender === 'object') {
      const ln = possibleSender.last_name || possibleSender.lastName || possibleSender.surname || null;
      const fn = possibleSender.first_name || possibleSender.firstName || possibleSender.given_name || null;
      if (ln || fn) return [ln, fn].filter(Boolean).join(' ');
      return possibleSender.full_name || possibleSender.name || possibleSender.username || null;
    }
    // otherwise fallback to flat fields
    const ln = n.sender_last_name || n.last_name || n.from_last_name || null;
    const fn = n.sender_first_name || n.first_name || n.from_first_name || null;
    if (ln || fn) return [ln, fn].filter(Boolean).join(' ');
    return n.sender || n.from || n.user || n.name || n.actor || n.sender_name || n.user_name || null;
  }

  // get a human-friendly display name for posts/users (copied from Home.jsx)
  function displayNameOf(item) {
    if (!item) return '';
    if (typeof item === 'string') return item;
    const tryKeys = ['author_display', 'author_full_name', 'author_fullname', 'authorFullName', 'author_name', 'authorName', 'full_name', 'fullName', 'name', 'displayName', 'username'];
    if (item.author) {
      const a = item.author;
      if (typeof a === 'string') {
        for (const k of tryKeys) if (item[k]) return item[k];
        return a;
      }
      if (typeof a === 'object') {
        for (const k of ['full_name', 'fullName', 'name', 'displayName', 'username']) if (a[k]) return a[k];
      }
    }
    for (const k of tryKeys) if (item[k]) return item[k];
    if (item.author && typeof item.author === 'string') return item.author;
    return '';
  }

  // helper to update local state (merge single post update) - used by modal actions
  function updatePostInState(id, updater) {
    setPosts(prev => prev.map(p => p.id === id ? updater(p) : p));
  }

  // Fetch a single post and ensure it is present in the main posts list (copied from Home.jsx)
  async function fetchPostById(id) {
    if (!apiUrl) return null;
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const res = await fetch(`${apiUrl}/api/posts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('fetch post failed');
      const d = await res.json();
      const p = d && (d.post || d) ? (d.post || d) : null;
      if (!p || !p.id) return null;
      const normalized = {
        id: p.id,
        author: p.author,
        author_first_name: p.author_first_name || (p.author && p.author.first_name) || null,
        author_last_name: p.author_last_name || (p.author && p.author.last_name) || null,
        author_display: p.author_display || p.author_full_name || p.author_fullname || p.full_name || (p.author && typeof p.author === 'object' && (p.author.full_name || p.author.fullName || p.author.name)) || null,
        authorRole: p.authorRole || p.role || '',
        authorSchoolId: p.authorSchoolId || p.author_school_id || null,
        title: p.title,
        content: p.content,
        createdAt: p.createdAt,
        likes: p.likes || [],
        comments: p.comments || [],
        approved: (typeof p.approved === 'boolean') ? p.approved : (p.approved === 1 || p.approved === '1'),
        attachments: (Array.isArray(p.attachments) ? p.attachments : (p.attachments ? (typeof p.attachments === 'string' ? JSON.parse(p.attachments) : p.attachments) : [])).map(a => normalizeAttachmentUrl(a))
      };
      setPosts(prev => {
        const exists = prev.some(x => x.id === normalized.id);
        if (exists) return prev.map(x => x.id === normalized.id ? normalized : x);
        return [normalized, ...prev];
      });
      return normalized;
    } catch (e) { console.error('fetchPostById failed', e); return null; }
  }

  async function fetchPending() {
    if (!apiUrl) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${apiUrl}/api/posts/pending`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('fetch pending failed');
      return await res.json();
    } catch (e) { console.error('Fetch pending failed', e); }
  }

  async function fetchPostsFromApi() {
    if (!apiUrl) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${apiUrl}/api/posts`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data && Array.isArray(data.posts)) {
        const normalized = data.posts.map(p => ({
          id: p.id,
          author: p.author,
          author_first_name: p.author_first_name || (p.author && p.author.first_name) || null,
          author_last_name: p.author_last_name || (p.author && p.author.last_name) || null,
          author_display: p.author_display || p.author_full_name || p.author_fullname || p.full_name || (p.author && typeof p.author === 'object' && (p.author.full_name || p.author.fullName || p.author.name)) || null,
          authorRole: p.authorRole || p.role || '',
          authorSchoolId: p.authorSchoolId || p.author_school_id || null,
          title: p.title,
          content: p.content,
          createdAt: p.createdAt,
          likes: p.likes || [],
          comments: p.comments || [],
          approved: (typeof p.approved === 'boolean') ? p.approved : (p.approved === 1 || p.approved === '1'),
          attachments: (Array.isArray(p.attachments) ? p.attachments : (p.attachments ? (typeof p.attachments === 'string' ? JSON.parse(p.attachments) : p.attachments) : [])).map(a => normalizeAttachmentUrl(a))
        }));
        setPosts(normalized);
      }
    } catch (e) { console.error('fetchPostsFromApi failed', e); }
  }

  function signalPendingChange() {
    try { localStorage.setItem('wolad_pending_signal', String(Date.now())); } catch (e) { }
  }

  function resolveAvatarUrl(src) {
    const raw = src || '';
    const s = String(raw || '');
    if (!s) return (apiUrl ? apiUrl.replace(/\/$/, '') + '/uploads/avt/avt-def.jpg' : '/uploads/avt/avt-def.jpg');
    if (normalizeUserUrl) return normalizeUserUrl(s);
    if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('//')) return s;
    if (s.startsWith('/')) {
      const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
      return base + s;
    }
    const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
    return base ? (base + '/' + s.replace(/^\//, '')) : s;
  }

  function extractActorMeta(n) {
    if (!n) return { name: null, avt: null };
    const possible = n.sender || n.from || n.user || n.actor || null;
    let name = getSenderName(n) || null;
    let avt = null;
    if (possible && typeof possible === 'object') {
      avt = possible.avt || possible.avatar || possible.photo || possible.profile_pic || possible.image || null;
      if (!name) {
        const ln = possible.last_name || possible.lastName || possible.surname || null;
        const fn = possible.first_name || possible.firstName || possible.given_name || null;
        if (ln || fn) name = [ln, fn].filter(Boolean).join(' ');
      }
    }
    // fallback fields
    avt = avt || n.sender_avt || n.sender_avatar || n.avt || n.avatar || null;
    return { name: name || null, avt: avt ? resolveAvatarUrl(avt) : resolveAvatarUrl(null) };
  }

  // Format a date/time consistently (show date + time). Use Vietnamese locale if available.
  function formatDateTime(value) {
    try {
      if (!value) return '';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString('vi-VN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'});
    } catch (e) { return String(value || ''); }
  }

  // build aggregated notifications for display
  function buildAggregated(notesInput) {
    if (!notesInput || !notesInput.length) return [];
    // bucket likes into 5-minute windows per post
    const buckets = {};
    const others = [];
    for (const n of notesInput) {
      const created = n.createdAt || n.created_at || n.created || null;
      const ts = created ? (new Date(created)).getTime() : (Date.now());
      // detect like notifications: heuristics - has ref to post and has sender and mentions like/action
      const isLike = (n.action === 'like') || (n.type === 'like') || (n.message && /like|thích|đã thích/i.test(n.message));
      const refId = n.refId || n.ref_id || (n.refType === 'post' || n.ref_type === 'post' ? (n.refId || n.ref_id) : null);
      if (isLike && refId) {
        // bucket key: postId + floor(ts/5min)
        const bucketKey = `${refId}_${Math.floor(ts / 300000)}`;
        if (!buckets[bucketKey]) buckets[bucketKey] = { refId, notes: [], ts: 0 };
        buckets[bucketKey].notes.push(n);
        buckets[bucketKey].ts = Math.max(buckets[bucketKey].ts, ts);
      } else {
        others.push(n);
      }
    }
  const aggregated = [];
    // convert buckets to aggregated messages
    for (const k of Object.keys(buckets)) {
      const b = buckets[k];
      const senders = Array.from(new Set(b.notes.map(x => getSenderName(x)).filter(Boolean)));
      const maxShow = 3;
      const visible = senders.slice(0, maxShow);
      const othersCount = Math.max(0, senders.length - maxShow);
      const post = posts.find(p => String(p.id) === String(b.refId));
      const postTitle = post ? post.title : (`bài viết #${b.refId}`);
      const msg = othersCount > 0 ? `${visible.join(', ')} và ${othersCount} người khác đã thích bài viết "${postTitle}" của bạn` : `${visible.join(', ')} liked your post "${postTitle}"`;
      // gather small metadata for the visible senders (avatars + names)
      const senderMetas = [];
      const seenNames = new Set();
      for (const noteItem of b.notes) {
        const meta = extractActorMeta(noteItem);
        if (!meta.name) continue;
        if (seenNames.has(meta.name)) continue;
        seenNames.add(meta.name);
        senderMetas.push(meta);
      }
      aggregated.push({ id: `agg_${k}`, type: 'like_agg', message: msg, count: senders.length, sourceIds: b.notes.map(x => x.id).filter(Boolean), refId: b.refId, createdAt: new Date(b.ts).toISOString(), unread: b.notes.some(x => !x.read), sendersMeta: senderMetas.slice(0, maxShow) });
    }
    // process others: for comments or items referencing posts, attach the post title to message
    const processedOthers = others.map(o => {
      const refId = o.refId || o.ref_id || (o.refType === 'post' || o.ref_type === 'post' ? (o.refId || o.ref_id) : null);
      const sender = getSenderName(o) || '';
      const actorMeta = extractActorMeta(o);
      // detect teacher-post-pending notification shape
      const isTeacherPostPending = ((o.type === 'post' || o.action === 'post') && /chờ duyệt|pending|unapproved|đợi duyệt/i.test(String(o.message || ''))) || ((o.refType === 'post' || o.ref_type === 'post') && /chờ duyệt|pending|unapproved|đợi duyệt/i.test(String(o.message || '')));
      if (refId) {
        const post = posts.find(p => String(p.id) === String(refId));
        const postTitle = post ? post.title : (`bài viết #${refId}`);
        // if it's likely a comment, produce a clearer message
        const isComment = (o.action === 'comment') || (o.type === 'comment') || (o.message && /comment|bình luận|đã bình luận/i.test(o.message));
        if (isComment) {
          return { ...o, message: `${sender} commented on your post"${postTitle}"`, unread: o.read !== true, createdAt: o.createdAt || o.created_at || o.created, actorMeta };
        }
          // If this looks like a teacher-created pending post and viewer is Mod, include teacher's class for clarity
          if (user && user.role === 'Mod' && isTeacherPostPending) {
            // derive a clear teacher display name (prefer last_name + first_name)
            let teacherName = null;
            const possibleActor = o.actor || o.user || o.sender || o.from || o.author || null;
            if (possibleActor && typeof possibleActor === 'object') {
              const ln = possibleActor.last_name || possibleActor.lastName || possibleActor.surname || null;
              const fn = possibleActor.first_name || possibleActor.firstName || possibleActor.given_name || null;
              if (ln || fn) teacherName = [ln, fn].filter(Boolean).join(' ');
              else teacherName = possibleActor.full_name || possibleActor.name || possibleActor.username || null;
            } else {
              // fallback to sender-derived name
              teacherName = sender || possibleActor || null;
            }

            // try to resolve teacher username for class mapping using common fields
            const teacherUsername = o.actor || o.user || o.sender || o.from || o.author || o.author_username || o.sender_username || null;
            const className = teacherUsername ? (teacherClassMap[teacherUsername] || null) : null;

            const teacherLabel = teacherName || teacherUsername || 'Giáo viên';
            const msg = `${teacherLabel} has submitted a new post pending approval${postTitle ? `: "${postTitle}"` : ''}`;
            return { ...o, message: msg, unread: o.read !== true, createdAt: o.createdAt || o.created_at || o.created, actorMeta, teacher_name: teacherName, class_name: className };
          }
          // otherwise append title if message exists
          const baseMsg = o.message || `${sender} has interacted`;
          return { ...o, message: `${baseMsg} — "${postTitle}"`, unread: o.read !== true, createdAt: o.createdAt || o.created_at || o.created, actorMeta };
      }
      return { ...o, unread: o.read !== true, createdAt: o.createdAt || o.created_at || o.created, actorMeta };
    });

    // merge others (non-like) and aggregated, sort by createdAt desc
    const merged = [...processedOthers, ...aggregated];
    merged.sort((a, b) => new Date(b.createdAt || b.created_at || Date.now()) - new Date(a.createdAt || a.created_at || Date.now()));
    return merged;
  }

  const aggregatedList = buildAggregated(notes);
  const hasUnread = aggregatedList.some(x => x.unread);

  return (
    <div className="notifications-page page-view">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom:20 }}>
        <h2 className="notifications-title">Notifications{hasUnread ? <span className="page-dot" aria-hidden="true"></span> : null}</h2>
        <div>
          <button className="btn" onClick={async () => {
            // optimistic
            setNotes(prev => prev.map(n => ({ ...n, read: true })));
            if (apiUrl) {
              try {
                const token = localStorage.getItem('token'); if (!token) return;
                await fetch(`${apiUrl}/api/notifications/read-all`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
              } catch (e) { console.error('Failed to mark all read', e); }
            }
          }}>Mark all read</button>
        </div>
      </div>
      {loading && <div className="muted">Loading...</div>}
      {(!notes || notes.length === 0) ? (
        <div className="card muted">No notifications</div>
      ) : (
        <div className="notification-list">
          {aggregatedList.map(n => {
            const actor = n.actorMeta || (n.actorMeta === undefined ? null : null);
            // derive teacher display name and class for pending-post items with fallbacks
            let teacherLabel = n.teacher_name || null;
            let className = n.class_name || null;
            if ((!teacherLabel || !className) && n.refId) {
              const referencedPost = (posts || []).find(p => String(p.id) === String(n.refId));
              if (referencedPost) {
                if (!teacherLabel) {
                  const ln = referencedPost.author_last_name || (referencedPost.author && (referencedPost.author.last_name || referencedPost.author.lastname)) || null;
                  const fn = referencedPost.author_first_name || (referencedPost.author && (referencedPost.author.first_name || referencedPost.author.firstname)) || null;
                  if (ln || fn) teacherLabel = [ln, fn].filter(Boolean).join(' ');
                  else teacherLabel = referencedPost.author_display || (referencedPost.author && (referencedPost.author.full_name || referencedPost.author.name)) || (referencedPost.author || null);
                }
                if (!className) {
                  const au = referencedPost.author || (referencedPost.author && referencedPost.author.username) || null;
                  if (au && teacherClassMap && teacherClassMap[au]) className = teacherClassMap[au];
                }
              }
            }
            return (
              <div key={n.id} className={"card notification-item" + (n.unread ? ' unread' : ' read')} onClick={() => onClickNotification(n)}>
                <div className="notification-top" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {/* avatars for aggregated likes */}
                  {n.type === 'like_agg' && Array.isArray(n.sendersMeta) && n.sendersMeta.length ? (
                    <div className="agg-avatars" style={{ display: 'flex', gap: -8, alignItems: 'center' }}>
                      {n.sendersMeta.map((s, i) => (
                        <img key={i} src={s.avt} alt={s.name || 'user'} style={{ width: 34, height: 34, borderRadius: 18, border: '2px solid white', boxShadow: '0 0 0 1px #ddd' }} />
                      ))}
                    </div>
                  ) : (actor && actor.avt ? (
                    <img src={actor.avt} alt={actor.name || 'user'} style={{ width: 40, height: 40, borderRadius: 20 }} />
                  ) : null)}

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <p className="notification-msg" style={{ margin: 0 }}>{n.message}</p>
                      <div className="notification-ts">{formatDateTime(n.createdAt || n.created_at || n.created)}</div>
                    </div>
                    {n.type === 'like_agg' && Array.isArray(n.sendersMeta) && n.sendersMeta.length ? (
                      <div className="muted" style={{ marginTop: 6 }}>{n.sendersMeta.map(s => s.name).filter(Boolean).join(', ')}{n.count > (n.sendersMeta || []).length ? ` and ${n.count - (n.sendersMeta || []).length} others` : ''}</div>
                    ) : null}
                    {/* Show teacher name + class for pending-post notifications when available */}
                    {(n.teacher_name || n.class_name) ? (
                      <div className="muted" style={{ marginTop: 6 }}>
                        {n.teacher_name ? n.teacher_name : null}{(n.class_name && n.teacher_name) ? ` — Class ${n.class_name}` : (n.class_name ? `Class ${n.class_name}` : '')}
                      </div>
                    ) : null}
                    {/* {n.unread ? <span className="dot" aria-hidden="true"></span> : null} */}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview / moderation modal for Mods - use exact Home.jsx modal markup */}
      {previewPost && (
        <div className="modal-backdrop" onClick={() => setPreviewPost(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{previewPost.title}</h3>
              <button onClick={() => setPreviewPost(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="muted">{(previewPost && (previewPost.author_last_name || previewPost.author_first_name)) ? [previewPost.author_last_name, previewPost.author_first_name].filter(Boolean).join(' ') : displayNameOf(previewPost)}{(user && user.role === 'Mod' && previewPost && previewPost.author && previewPost.authorRole === 'Teacher' && teacherClassMap && teacherClassMap[previewPost.author]) ? ` — ${teacherClassMap[previewPost.author]}` : ''} — {previewPost.createdAt ? formatDateVN(previewPost.createdAt) : ''}</div>
              <p style={{ marginTop: 12 }}>{previewPost.content}</p>
              {previewPost.attachments && previewPost.attachments.length > 0 && (
                <div className="attachment-grid mt-8">
                  {previewPost.attachments.map((a, idx) => (
                    <div key={idx} className="att" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(previewPost.attachments); setLightboxIndex(idx); setLightboxOpen(true); }}>
                      {a.mime && a.mime.startsWith('image/') && <img src={a.url || a.path} alt={a.filename || ''} />}
                      {a.mime && a.mime.startsWith('video/') && <video src={a.url || a.path} />}
                      {(!a.mime || (!a.mime.startsWith('image/') && !a.mime.startsWith('video/'))) && <a href={a.url || a.path} target="_blank" rel="noreferrer">{a.filename || 'Tệp'}</a>}
                    </div>
                  ))}
                </div>
              )}
              {/* comments removed for pending preview */}
            </div>
            <div className="modal-actions">
              {user.role === 'Mod' && (
                <>
                  <button className="btn approve" onClick={async () => {
                    try {
                      const token = localStorage.getItem('token'); const res = await fetch(`${apiUrl}/api/posts/${previewPost.id}/approve`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }); if (!res.ok) throw new Error('approve failed'); await res.json(); updatePostInState(previewPost.id, x => ({ ...x, approved: true })); setPreviewPost(null); try { await fetchPostById(previewPost.id); await fetchPending(); await fetchPostsFromApi(); } catch (e) { /* ignore */ } if (window.showToast) window.showToast('Approved successfully', 'success'); // reload page for teachers so they immediately see the approved post
                      if (user && user.role === 'Teacher') { try { window.location.reload(); } catch (e) { } }
                    } catch (e) { console.error(e); if (window.showToast) window.showToast('Approve failed', 'error'); }
                  }}>Approve</button>
                  <button className="btn reject" onClick={async () => { if (!window.confirm('Reject post?')) return; try { const token = localStorage.getItem('token'); const res = await fetch(`${apiUrl}/api/posts/${previewPost.id}/reject`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }); if (!res.ok) throw new Error('reject failed'); updatePostInState(previewPost.id, p => ({ ...p, approved: false })); setPreviewPost(null); if (window.showToast) window.showToast('Rejected successfully', 'success'); } catch (e) { console.error(e); if (window.showToast) window.showToast('Rejected failed', 'error'); } }}>Reject</button>
                </>
              )}
              <button className="btn btn-muted" onClick={() => setPreviewPost(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {lightboxOpen && <Lightbox items={lightboxItems} startIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />}
    </div>
  );
}
